import { useToast } from 'primevue/usetoast';
import { toUserMessage } from '@/utils/error';
import {
  useSubjectsQuery,
  useDownloadScoreSheetLazyQuery,
  useImportScoreSheetMutation,
} from '@/types/vue-apollo-helpers';
import type {
  DownloadScoreSheetQueryVariables,
  ImportScoreSheetMutation,
  ImportScoreSheetMutationVariables,
} from '@/types/operations';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';
import { useAcademicCalendar } from '@/composables/queries/useAcademicCalendar';
import { useStaffDashboard } from '@/composables/useStaffDashboard';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';

// Aliases over the generated operation types so components never hand-roll
// the result shape.
export type ScoreImportResult = NonNullable<ImportScoreSheetMutation['importScoreSheet']>;
export type ScoreImportSummary = NonNullable<ScoreImportResult['summary']>;
export type ScoreImportRowIssue = ScoreImportSummary['issues'][number];

export function useScoreEntry() {
  const toast = useToast();
  const userStore = useUserStore();
  const { currentUser } = storeToRefs(userStore);

  // State
  const classroomId = ref('');
  const subjectId = ref('');
  const academicCalendarId = ref('');
  const programmeId = ref('');
  const downloading = ref(false);
  const uploading = ref(false);
  const selectedFile = ref<File | null>(null);
  const isDragging = ref(false);
  const fileInput = ref<HTMLInputElement | null>(null);

  // Preview (dry-run) + result state
  const previewing = ref(false);
  const previewSummary = ref<ScoreImportSummary | null>(null);
  const previewError = ref<string | null>(null);
  const importResult = ref<ScoreImportResult | null>(null);
  const showResultDialog = ref(false);

  // Data Fetching
  const { result: subjectsResult, loading: loadingSubjects } = useSubjectsQuery();
  const { load: loadDownloadUrl, refetch: refetchDownloadUrl } = useDownloadScoreSheetLazyQuery();
  const { mutate: importScores } = useImportScoreSheetMutation();

  const { labels } = useSchoolLabels();
  const { isTertiary } = useSchoolFeatures();
  const { levelForId } = useTertiaryLevels();
  const { activeAcademicCalendar } = useAcademicCalendar({ activeAcademicCalendar: { autoFetch: true } });
  const { allMyCourses, myCourses } = useStaffDashboard();

  const isCourseContext = computed(() => labels.value.SUBJECT === 'Course' || isTertiary.value);

  const subjectOptions = computed(() => subjectsResult.value?.subjects || []);

  const isAdmin = computed(() =>
    can(currentUser.value, AppPermissions.Permissions.Actions.View, AppPermissions.Permissions.Resource),
  );

  const myCourseOptions = computed(() =>
    myCourses.value.flatMap((lc) => {
      const course = lc.course;
      if (!course) return [];
      return [{
        label: `${course.course_code} — ${course.course_title} (${course.unit} units)`,
        value: course.id,
        code: course.course_code,
        title: course.course_title,
        unit: course.unit,
        instance: course.course_instance_id,
      }];
    }),
  );

  const selectedSubjectName = computed(() => {
    if (isCourseContext.value && !isAdmin.value) {
      const opt = myCourseOptions.value.find((c) => c.value === subjectId.value);
      return opt ? `${opt.code} — ${opt.title}` : undefined;
    }
    return subjectOptions.value.find((s) => s.id === subjectId.value)?.name;
  });

  const canDownload = computed(() => {
    if (!subjectId.value || !academicCalendarId.value) return false;
    if (!isTertiary.value && !classroomId.value) return false;
    return true;
  });

  const hasScorePermission = computed(() =>
    can(currentUser.value, AppPermissions.Results.Actions.UploadScores, AppPermissions.Results.Resource));

  // The score views default to the active calendar; uploading into another
  // calendar is legitimate (late entry) but worth a visible heads-up so the
  // user knows where the scores will be visible afterwards.
  const calendarMismatch = computed(() => {
    const activeId = activeAcademicCalendar.currentCalendar.value?.id;
    if (!activeId || !academicCalendarId.value) return false;
    return String(activeId) !== String(academicCalendarId.value);
  });

  // "View the scores you just uploaded" — only resolvable when the uploader is
  // an approved lecturer of the course (the Course Students view is gated).
  const lecturerCourseRoute = computed(() => {
    if (!isTertiary.value || !subjectId.value) return null;
    const lecturerCourse = allMyCourses.value.find(
      (lc: { course?: { id?: string | number | null } | null; is_approved?: boolean | null }) =>
        String(lc.course?.id) === String(subjectId.value) && lc.is_approved,
    );
    if (!lecturerCourse) return null;
    return { name: 'admin-course-students', params: { courseId: String(subjectId.value) } };
  });

  // Actions
  async function downloadSheet() {
    if (!canDownload.value) return;
    downloading.value = true;
    try {
      const resolvedLevel = isTertiary.value ? levelForId(classroomId.value) : null;
      const variables: DownloadScoreSheetQueryVariables = {
        subject_id: subjectId.value,
        academic_calendar_id: academicCalendarId.value,
        ...(classroomId.value ? { classroom_id: classroomId.value } : {}),
        ...(programmeId.value ? { programme_id: programmeId.value } : {}),
        ...(resolvedLevel ? { level: resolvedLevel } : {}),
      };

      const loadResult = await loadDownloadUrl(null, variables);

      let exportRecord: { id?: string } | undefined;

      if (loadResult) {
        exportRecord = loadResult?.downloadScoreSheet;
      } else {
        const refetchResult = await refetchDownloadUrl(variables);
        exportRecord = refetchResult?.data?.downloadScoreSheet;
      }

      if (exportRecord && exportRecord.id) {
        toast.add({ severity: 'success', summary: 'Export Started', detail: 'Your score sheet is being generated. Check the Downloads icon in the top bar for progress.', life: 5000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to initiate export.', life: 3000 });
      }
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Error initiating export: ' + (e instanceof Error ? e.message : 'Unknown error'), life: 3000 });
    } finally {
      downloading.value = false;
    }
  }

  function triggerFileSelect() {
    fileInput.value?.click();
  }

  function isExcelFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls');
  }

  // The same check for picked and dropped files: the picker's "All files" option
  // would otherwise send a PDF/CSV to the server and come back as a raw error.
  function acceptFile(file: File | undefined) {
    if (file && isExcelFile(file)) {
      selectedFile.value = file;
    } else if (file) {
      toast.add({ severity: 'warn', summary: 'Invalid File', detail: 'Please upload a valid Excel file (.xlsx, .xls)', life: 5000 });
    }
  }

  // Let the same file be picked again after it was cleared or rejected.
  function resetFileInput() {
    if (fileInput.value) fileInput.value.value = '';
  }

  function onFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    acceptFile(target.files?.[0]);
    resetFileInput();
  }

  function onDrop(event: DragEvent) {
    isDragging.value = false;
    acceptFile(event.dataTransfer?.files?.[0]);
  }

  function formatFileSize(bytes: number) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function importVariables(dryRun: boolean): Record<string, unknown> {
    const vars: Record<string, unknown> = {
      file: selectedFile.value,
      subject_id: subjectId.value,
      academic_calendar_id: academicCalendarId.value,
      dry_run: dryRun,
    };
    if (classroomId.value) vars.classroom_id = classroomId.value;
    return vars;
  }

  // Automatic dry-run preview: the moment a file and a complete selection are
  // both present, ask the server what the sheet WOULD do — without writing —
  // so problems (wrong sheet, unmatched students, edited headers) surface
  // before the user commits.
  async function previewSheet() {
    if (!selectedFile.value || !canDownload.value) return;
    previewing.value = true;
    previewError.value = null;
    previewSummary.value = null;
    try {
      // importVariables is assembled dynamically (classroom_id is optional), so
      // assert the finished mutation-variables shape.
      const res = await importScores(importVariables(true) as ImportScoreSheetMutationVariables);
      previewSummary.value = res?.data?.importScoreSheet?.summary ?? null;
      if (!previewSummary.value) {
        previewError.value = res?.data?.importScoreSheet?.message ?? null;
      }
    } catch (e) {
      // Preview failures (tampered sheet, wrong course/calendar, unknown
      // assessment columns) carry user-facing messages from the server.
      previewError.value = toUserMessage(e, 'Could not preview this file. Download a fresh score sheet and try again.');
    } finally {
      previewing.value = false;
    }
  }

  watch([selectedFile, subjectId, academicCalendarId, classroomId], () => {
    previewSummary.value = null;
    previewError.value = null;
    if (selectedFile.value && canDownload.value) {
      void previewSheet();
    }
  });

  async function uploadSheet() {
    if (!selectedFile.value || !canDownload.value) return;

    uploading.value = true;
    try {
      const res = await importScores(importVariables(false) as ImportScoreSheetMutationVariables);
      const payload = res?.data?.importScoreSheet ?? null;
      importResult.value = payload;

      if (payload?.summary) {
        showResultDialog.value = true;
      } else if (payload?.success) {
        toast.add({ severity: 'success', summary: 'Uploaded', detail: payload.message || 'Scores uploaded successfully!', life: 5000 });
      } else {
        toast.add({ severity: 'warn', summary: 'Warning', detail: payload?.message || 'Upload completed with warnings.', life: 5000 });
      }
      // The upload has finished either way; the result dialog explains what happened,
      // so clear the file rather than leave a stale one ready to be sent again.
      selectedFile.value = null;
      resetFileInput();
    } catch (e) {
      toast.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: toUserMessage(e, 'We couldn\'t upload this score sheet. Please try again.'),
        life: 8000,
      });
    } finally {
      uploading.value = false;
    }
  }

  function downloadIssuesCsv() {
    const issues = importResult.value?.summary?.issues ?? previewSummary.value?.issues ?? [];
    if (issues.length === 0) return;

    const escape = (v: string) => '"' + v.replace(/"/g, '""') + '"';
    const lines = ['Row,Student,Reason'];
    for (const issue of issues) {
      lines.push([String(issue.row), escape(issue.student_ref ?? ''), escape(issue.reason)].join(','));
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skipped-score-rows.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return {
    currentUser,
    classroomId,
    subjectId,
    academicCalendarId,
    programmeId,
    downloading,
    uploading,
    selectedFile,
    isDragging,
    fileInput,
    loadingSubjects,
    labels,
    isCourseContext,
    isAdmin,
    myCourseOptions,
    subjectOptions,
    selectedSubjectName,
    canDownload,
    hasScorePermission,
    calendarMismatch,
    lecturerCourseRoute,
    previewing,
    previewSummary,
    previewError,
    importResult,
    showResultDialog,
    downloadSheet,
    triggerFileSelect,
    onFileChange,
    onDrop,
    formatFileSize,
    previewSheet,
    uploadSheet,
    downloadIssuesCsv,
  };
}
